import { useNavigate } from 'react-router-dom';
import { useConfirm } from '@/features/common/ConfirmDialog/useConfirm';
import { communityApi } from '@/features/publicsite/community/api/communityApi';
import type { SpaceMember } from '@/features/publicsite/types/community/SpaceMember';
import { publicCommunity } from '@/i18n/dictionaries/publicCommunity';
import { useTranslation } from '@/i18n/useTranslation';
import { detailOr } from '@/services/api';

/**
 * Where "Comunitatea mea" reads that the reader has just left a space, and which one — the
 * navigation state its LeftSpaceState type names; the two features share the word, not a module.
 */
const LEFT_SPACE_STATE = 'leftSpace';

/**
 * The owner removing a member and a member leaving, each asked first. A refusal is the server's
 * sentence on the page's status line. Leaving takes the reader to "Comunitatea mea", which says
 * what they left: the space's page is no longer theirs to read.
 */
export function useSpaceMembership(
  forumId: string,
  forumTitle: string,
  feedback: {
    succeed: (message: string) => void;
    fail: (message: string) => void;
    reloadMembers: () => Promise<void>;
  },
) {
  const { t } = useTranslation(publicCommunity);
  const navigate = useNavigate();
  const { confirm, dialog } = useConfirm();

  const remove = async (member: Pick<SpaceMember, 'userId' | 'fullName'>) => {
    const confirmed = await confirm({
      title: t('removeConfirmTitle'),
      body: t('removeConfirmBody', { name: member.fullName }),
      confirmLabel: t('removeMember'),
      tone: 'danger',
    });
    if (!confirmed) return;
    try {
      await communityApi.removeMember(forumId, member.userId);
      feedback.succeed(t('memberRemoved', { name: member.fullName }));
      await feedback.reloadMembers();
    } catch (e) {
      feedback.fail(detailOr(e, t('removeError')));
    }
  };

  const leave = async () => {
    const confirmed = await confirm({
      title: t('leaveConfirmTitle'),
      body: t('leaveConfirmBody'),
      confirmLabel: t('leaveSpace'),
      tone: 'danger',
    });
    if (!confirmed) return;
    try {
      await communityApi.leave(forumId);
      navigate('/app/comunitate', { state: { [LEFT_SPACE_STATE]: forumTitle } });
    } catch (e) {
      feedback.fail(detailOr(e, t('leaveError')));
    }
  };

  return { remove, leave, confirmDialog: dialog };
}
